import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, ListFilter, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useLocation, useRoute } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";
import ModifierGroupEditor from "@/components/pos/admin/modifier-group-editor";
import ModifierEditor from "@/components/pos/admin/modifier-editor";
import ModifierList from "@/components/pos/admin/modifier-list";

const fetchModifierGroups = async () => {
  const res = await posApiFetch("/v1/pos/modifier-groups");
  if (!res.ok) throw new Error("Failed to fetch modifier groups");
  return res.json();
};

type ModifierGroupsState = {
  status?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
};

const defaultModifierGroupsState: ModifierGroupsState = {
  status: "all",
  currentPage: 1,
  itemsPerPage: 10,
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export default function ModifierGroupsPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [isAddRoute] = useRoute("/admin/modifier-groups/add");
  const [isEditRoute, editParams] = useRoute("/admin/modifier-groups/:id/edit");
  const [isModifiersRoute, modifiersParams] = useRoute("/admin/modifier-groups/:id/modifiers");
  const [isModifierAddRoute, modifierAddParams] = useRoute("/admin/modifier-groups/:id/modifiers/add");
  const [isModifierEditRoute, modifierEditParams] = useRoute("/admin/modifier-groups/:id/modifiers/:modId/edit");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => { document.title = "Modifier Groups | SECH Admin"; }, []);

  const [modifierGroupsState, setModifierGroupsState] = usePagePersistentState<ModifierGroupsState>("modifierGroups", defaultModifierGroupsState);
  const searchQuery = modifierGroupsState.searchQuery;
  const currentPage = modifierGroupsState.currentPage;
  const itemsPerPage = modifierGroupsState.itemsPerPage;
  const statusFilter = modifierGroupsState.status;
  const dateRange = modifierGroupsState.dateRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "modifier_groups_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "", dateRange: { from: "", to: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter ?? "",
      dateRange: { from: dateRange?.from ?? "", to: dateRange?.to ?? "" },
    }));
  }, [statusFilter, dateRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setModifierGroupsState((prev) => ({
      ...prev,
      status: values.status || "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
    }));
  };

  const { data: modifierGroups = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/modifier-groups"],
    queryFn: fetchModifierGroups,
    retry: 3,
    retryDelay: 1000,
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/modifier-groups/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete modifier group");
      toast({ title: "Modifier group deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/modifier-groups"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete modifier group", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (modifierGroupsState.status && modifierGroupsState.status !== "all") count += 1;
    const fromDate = normalizeDate(modifierGroupsState.dateRange?.from);
    const toDate = normalizeDate(modifierGroupsState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    return count;
  };

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const modifierGroupFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Option" },
    { key: "dateRange", label: "Date Range", type: "dateRange" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "all", dateRange: { from: "", to: "" } });
    setModifierGroupsState((prev) => ({ ...prev, status: "all", dateRange: { from: "", to: "" }, currentPage: 1 }));
  };

  const setTableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || ""} imageUrl={row?.imageUrl} />
          <div className="flex flex-col">
            <span className="text-[13px] text-[#505258]">{value}</span>
            {row.displayName && row.displayName !== value && (
              <span className="text-[11px] text-[var(--secondary-text-color)]">{row.displayName}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      label: "Status", field: "active", sortable: true,
      cellTemplate: (value: any) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Selection Type", field: "selectionType", sortable: true,
      cellTemplate: (value: string) => (
        <span className="text-[13px] text-[var(--secondary-text-color)]">
          {value === "single" ? "Single" : value === "multiple" ? "Multiple" : value || "-"}
        </span>
      ),
    },
    {
      label: "Min Selections", field: "minSelections", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "-"}</span>,
    },
    {
      label: "Max Selections", field: "maxSelections", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "-"}</span>,
    },
    {
      label: "Modifiers", field: "modifierCount",
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? 0}</span>,
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/modifier-groups/${r.id}/edit`) },
            { label: "View Modifiers", icon: Eye, onClick: (r: any) => navigate(`/admin/modifier-groups/${r.id}/modifiers`) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredModifierGroups = useMemo(() =>
    modifierGroups.filter((group: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const status = group.active ? "active" : "inactive";
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesSearch = !search || group.name?.toLowerCase().includes(search) || group.displayName?.toLowerCase().includes(search) || group.description?.toLowerCase().includes(search);
      const fromDate = normalizeDate(modifierGroupsState.dateRange?.from);
      const toDate = normalizeDate(modifierGroupsState.dateRange?.to);
      const createdAt = new Date(group.createdAt).getTime();
      const matchesDateRange = fromDate && toDate ? createdAt >= fromDate.getTime() && createdAt <= toDate.getTime() : true;
      return matchesStatus && matchesSearch && matchesDateRange;
    }), [modifierGroups, statusFilter, debouncedSearch, modifierGroupsState.dateRange]
  );

  const sortedModifierGroups = useMemo(() => {
    return [...filteredModifierGroups].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "active": aVal = a?.active ? 1 : 0; bVal = b?.active ? 1 : 0; break;
        case "selectionType": aVal = a?.selectionType ?? ""; bVal = b?.selectionType ?? ""; break;
        case "minSelections": aVal = a?.minSelections ?? 0; bVal = b?.minSelections ?? 0; break;
        case "maxSelections": aVal = a?.maxSelections ?? 0; bVal = b?.maxSelections ?? 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.name ?? ""; bVal = b?.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredModifierGroups, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedModifierGroups.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedModifierGroups = sortedModifierGroups.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setModifierGroupsState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setModifierGroupsState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedModifierGroups.length, handlePageChange, handleItemsPerPageChange };

  // Sub-route handling
  if (isAddRoute) {
    return <ModifierGroupEditor onBack={() => navigate("/admin/modifier-groups")} />;
  }

  if (isEditRoute) {
    return <ModifierGroupEditor modifierGroupId={editParams?.id} onBack={() => navigate("/admin/modifier-groups")} />;
  }

  if (isModifierAddRoute) {
    const gId = modifierAddParams?.id ?? "";
    return <ModifierEditor groupId={gId} onBack={() => navigate(`/admin/modifier-groups/${gId}/modifiers`)} />;
  }

  if (isModifierEditRoute) {
    const gId = modifierEditParams?.id ?? "";
    return <ModifierEditor groupId={gId} modifierId={modifierEditParams?.modId} onBack={() => navigate(`/admin/modifier-groups/${gId}/modifiers`)} />;
  }

  if (isModifiersRoute) {
    const gId = modifiersParams?.id ?? "";
    return <ModifierList groupId={gId} onBack={() => navigate("/admin/modifier-groups")} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Modifier Groups</h2>
          <p className="text-gray-600 mb-4">Failed to load modifier groups</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Modifier Groups</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setModifierGroupsState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/modifier-groups/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Modifier Group
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-0 h-[calc(100vh-130px)]">
          {!paginatedModifierGroups.length && !isLoading ? (
            <NoRecordFound title="Modifier Groups" description="There are no modifier groups available." action={
              <Button onClick={() => navigate("/admin/modifier-groups/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Modifier Group
              </Button>
            } />
          ) : (
            <CommonGrid
              rowData={paginatedModifierGroups}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={modifierGroupFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Modifier Group" description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`} confirmText="Delete" variant="delete" />
    </div>
  );
}
